import { AxiosResponse } from "axios";

import {
  IAddWardMeasurementTypeRequest,
  IApplyMeasurementTypeTemplateRequest,
  IMeasurementTypeCatalogItem,
  IMeasurementTypeTemplate,
  IUpdateWardMeasurementTypeRequest,
  IWardMeasurementType,
} from "../../@types/admin/AdminMeasurementType";
import { api } from "../api";

/**
 * Ward × Measurement Types admin page (EN-5157), migrated from the Retool
 * WardMeasurementTypes page and its module.
 *
 * The write endpoints are targeted rather than whole-set: add one code,
 * update one code's flags, remove one code. Only ApplyTemplate can replace
 * the ward's whole configuration, and only when `overwrite` is set.
 */
export const measurementTypesService = {
  /** The ward's configured measurement types. */
  async getWardMeasurementTypes(
    wardId: number
  ): Promise<AxiosResponse<IWardMeasurementType[]>> {
    return api.get<IWardMeasurementType[]>(
      `/Retool/Wards/${wardId}/MeasurementTypes`
    );
  },

  /** Catalogue entries NOT yet on the ward — the add picker's options. */
  async getAvailableMeasurementTypes(
    wardId: number
  ): Promise<AxiosResponse<IMeasurementTypeCatalogItem[]>> {
    return api.get<IMeasurementTypeCatalogItem[]>(
      `/Retool/Wards/${wardId}/MeasurementTypes/Available`
    );
  },

  /** Active templates with their items. */
  async getMeasurementTypeTemplates(): Promise<
    AxiosResponse<IMeasurementTypeTemplate[]>
  > {
    return api.get<IMeasurementTypeTemplate[]>(
      "/Retool/MeasurementTypes/Templates"
    );
  },

  async addWardMeasurementType(
    wardId: number,
    request: IAddWardMeasurementTypeRequest
  ): Promise<AxiosResponse<IWardMeasurementType>> {
    return api.post<IWardMeasurementType>(
      `/Retool/Wards/${wardId}/MeasurementTypes`,
      request
    );
  },

  async updateWardMeasurementType(
    wardId: number,
    code: string,
    request: IUpdateWardMeasurementTypeRequest
  ): Promise<AxiosResponse<IWardMeasurementType>> {
    return api.put<IWardMeasurementType>(
      `/Retool/Wards/${wardId}/MeasurementTypes/${encodeURIComponent(code)}`,
      request
    );
  },

  /** Returns the ward's remaining configuration. */
  async removeWardMeasurementType(
    wardId: number,
    code: string
  ): Promise<AxiosResponse<IWardMeasurementType[]>> {
    return api.delete<IWardMeasurementType[]>(
      `/Retool/Wards/${wardId}/MeasurementTypes/${encodeURIComponent(code)}`
    );
  },

  /** Returns the ward's configuration after the template is applied. */
  async applyMeasurementTypeTemplate(
    wardId: number,
    request: IApplyMeasurementTypeTemplateRequest
  ): Promise<AxiosResponse<IWardMeasurementType[]>> {
    return api.post<IWardMeasurementType[]>(
      `/Retool/Wards/${wardId}/MeasurementTypes/ApplyTemplate`,
      request
    );
  },
};
